import Map from 'ol/Map';
import View from 'ol/View';
import { get as getProjection, fromLonLat } from 'ol/proj';
import TileLayer from 'ol/layer/Tile';
import TileWMS from 'ol/source/TileWMS';
import { Google, Vector as VectorSource } from 'ol/source';
import { defaults as defaultControls, FullScreen, ScaleLine } from 'ol/control';
import OSM from 'ol/source/OSM';
import GeoJSON from 'ol/format/GeoJSON';
import VectorLayer from 'ol/layer/Vector';
import { Group as LayerGroup } from 'ol/layer';
import Overlay from 'ol/Overlay';
import { get } from 'app/utils/lo/lo';
import { Point } from 'ol/geom';
import Feature from 'ol/Feature';
import MeasureTool from 'app/utils/maps/tools/measureToolUtils';
import DrawingTool from 'app/utils/maps/tools/mapDrawingToolsUtils';
import RouteHelperTool from 'app/utils/maps/tools/mapRouteUtils';
import { bind, memoize } from 'app/utils/decorators/decoratorUtils';
import { Circle as CircleStyle, Fill, Stroke, Style, Text } from 'ol/style';
import { getSelectedLayerProperty, isLayerSelected } from 'app/utils/maps/layer/layerUtils';
import { isDefined } from 'app/utils/utils';
import mdiMapping from 'app/components/molecules/Map/EntityPin/entitiesPinMap.json';
import NorthControl from 'app/utils/maps/controller/NorthControl';
import ScaleControl from 'app/utils/maps/controller/ScaleControl';
import GpsControl from 'app/utils/maps/controller/GpsControl';
import OLCesium from 'olcs/OLCesium';
import DoubleClickZoom from 'ol/interaction/DoubleClickZoom';
import ClusterLayer from 'app/containers/Maps/EntityLayerStyling/ClusterLayer';
import EntityLayer from 'app/containers/Maps/EntityLayerStyling/EntityLayer';
import 'style/maps/olToolStyles.css';
import { entityPinStyle, singleFeatureStyle } from 'app/components/molecules/Map/EntityPin/entityPinStyle';
import Modify from 'ol/interaction/Modify';
import Collection from 'ol/Collection';
import LineString from 'ol/geom/LineString';
import Select from 'ol/interaction/Select';
import RelatedEntityLayer from '../EntityLayerStyling/RelatedEntityLayer'; //module;
import HeatMapLayer from '../EntityLayerStyling/HeatMapLayer';
import * as turf from '@turf/turf';
import DrawingLayer from 'app/containers/Maps/EntityLayerStyling/DrawingLayer';
import ThreeDLayer from '../EntityLayerStyling/ThreeDLayer';
import Geocode from 'app/utils/maps/geocodeUtils';
import cesiumLayerConfig from 'app/config/cesiumLayerConfig';
import { click } from 'ol/events/condition';
import customIconSet from 'app/components/molecules/Map/EntityPin/customEntityPin.js';
import { create3DIconCanvas, MAP_MARKER_LAYER, DEFAULT_ZOOM_LEVEL } from 'app/utils/maps/layer/layerUtils';
import { getAttachmentUrl } from 'app/utils/attachments/attachmentsUtils';
import { rocketHost } from 'app/utils/env';
import { Heatmap } from 'ol/layer.js';
import * as Cesium from 'cesium';
import ReplayLayer from 'app/containers/Maps/LayerAbout/ReplayLayer/ReplayLayer';
import { drawXYZLayer } from 'app/components/molecules/Map/DrawShapes/xyzLayerUtlis';
import 'cesium/Source/Widgets/widgets.css';
import { render, compile } from 'app/utils/template/template';
import google_on_white from 'assets/images/maps/google_on_white_hdpi.png';
import Control from 'ol/control/Control.js';
import { changeBaseLayer } from 'app/utils/maps/layer/layerUtils';
import {
    calculateLayerZIndex,
    calculateSubLayerZIndex,
    applyLayerZIndex
} from 'app/config/layerZIndexConfig';

window.Cesium = Cesium; // expose Cesium to the OL-Cesium library

class GoogleLogoControl extends Control {
    constructor() {
        const element = document.createElement('img');
        element.style.pointerEvents = 'none';
        element.style.position = 'absolute';
        element.style.bottom = '14px';
        element.style.left = '14px';
        element.style.zIndex = '1002';
        element.style.width = '100px';
        element.src = google_on_white;

        super({
            element: element,
        });
    }
}

class olMap {
    overlayMap = [];
    map = {};
    view = {};
    baseLayers = [];
    overlayLayers = [];
    customInteractions = {};
    lastInteractionType = null;
    routeHelperTool = {};
    _ol3d;
    is3DVisible = false;
    enable3D = false;
    defaultPreferences = {};
    entityLayers = {};
    IamHereLayer;
    dragDropFeatures;
    targetID;
    select;
    eraserStatus;
    isHeatMap = [];
    isRelatedMap = [];

    constructor() {
        this.customInteractions = {
            measurement: new MeasureTool(this),
            drawing: new DrawingTool(this),
        };
        this.routeHelperTool = new RouteHelperTool(this);
    }

    @bind
    initMapPanel(targetId, title) {
        this.targetID = targetId;
        const controls = [
            new FullScreen(),
            new NorthControl({ olMapVM: this }),
            new ScaleControl({ olMapVM: this }),
            new GpsControl({ olMapVM: this }),
            new ScaleLine({
                className: 'custom_scale',
                units: 'metric',
                bar: false,
            }),
        ];
        this.setMap(targetId, controls, title);

        for (const key in this.customInteractions) {
            this.customInteractions[key].initTool();
        }
        const dblClickZoom = this.getMap()
            .getInteractions()
            .getArray()
            .find(interaction => interaction instanceof DoubleClickZoom);
        if (dblClickZoom) {
            this.getMap().removeInteraction(dblClickZoom);
        }
        this.addHighlightLayer();
    }

    @bind
    initEntityAboutMap(targetId, locationMap) {
        this.setMap(targetId, [], locationMap);
    }

    @bind
    initPrimaryAboutSvgMap(targetId, locationMap) {
        this.setMap(targetId, [], locationMap);
    }

    @bind
    setOpacity(opacity) {
        return isDefined(opacity) ? opacity : 1;
    }

    @bind
    setDefaultPreferences(pref) {
        const { name, layers = [] } = pref || {};
        const baseLayers = {
            layers: [
                {
                    title: 'OSM',
                    visible: false,
                    opacity: 1,
                },
            ],
        };

        baseLayers.layers = name && baseLayers.layers.map(lyr => ({ ...lyr, visible: lyr.title === name }));
        this.defaultPreferences = {
            baseLayers,
            allLayers: {
                layers,
            },
        };
    }

    @bind
    addWMSPreviewLayer({ url, layer, extent, addToBaseLayers }) {
        const updatedUrl = url.split('?')[0];
        const previewWMSLayer = this.getMap()
            .getLayers()
            .array_.find(lyr => lyr.values_.title === 'Preview WMS Layer');
        if (previewWMSLayer) {
            this.getMap().removeLayer(previewWMSLayer);
        }

        const zIndex = addToBaseLayers ? 1 : this.getNextLayerZIndex();
        
        const wmsLayer = new TileLayer({
            title: 'Preview WMS Layer',
            source: new TileWMS({
                url: updatedUrl,
                params: {
                    LAYERS: layer,
                    TILED: true,
                },
                crossOrigin: 'anonymous',
            }),
            zIndex: zIndex,
            baseLayer: addToBaseLayers,
        })
        
        if(addToBaseLayers){
            const allLayers = this.getMap()
                ?.getLayers()?.getArray() || [];
            const baseLayersGroup = allLayers.find(
                l => l.get('title') === 'baseLayers'
            );
            baseLayersGroup.getLayers().push(wmsLayer)
            this.updateMap();
        } else {
            this.getMap().addLayer(wmsLayer);
            this.zoomToSavedMapExtent(extent);
            this.updateMap();
        }
    }

    @bind
    async addXYZPreviewLayer({ layerId, url, apiKey, addToBaseLayers, projection }) {
        if (!url) {
            return 'URL is required';
        }
        
        const map = this.getMap();
        const urlApiKey = render(compile(url), {apikey: apiKey});

        const previewXYZLayer = this.findLayerById(layerId);
        const existingOpacity = previewXYZLayer ? previewXYZLayer.getOpacity() : 1;
        const existingVisibility = previewXYZLayer ? previewXYZLayer.getVisible() : true;

        if (previewXYZLayer) {
            this.removeLayerFromAllLayersById(layerId);
        }
        
        const zIndex = addToBaseLayers ? 1 : this.getNextLayerZIndex();
        
        const xyzLayer = drawXYZLayer({
            _attributes: {
                id: layerId,
                name: 'Preview XYZ Layer',
                url: urlApiKey,
                apiKey,
                visible: existingVisibility,
                opacity: existingOpacity,
                projection,
                zIndex: zIndex,
                baseLayer: addToBaseLayers,
            }
        });

        xyzLayer.setVisible(existingVisibility);
        applyLayerZIndex(xyzLayer, zIndex);
        
        const baseLayersGroup = this.getLayersGroup('baseLayers');
        const allLayersGroup = this.getLayersGroup('allLayers');

        map.addLayer(xyzLayer);
        
        if (addToBaseLayers) {
            baseLayersGroup.getLayers().push(xyzLayer);
        } else {
            allLayersGroup.getLayers().push(xyzLayer);
        }

        map.getView().setProperties({
            projection: getProjection('EPSG:3857')
        });
        
        this.updateMap();

        const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('Timeout')), 10000); 
        });
    
        const loadPromise = new Promise((resolve, reject) => {
            const source = xyzLayer.getSource();
            
            source.on('tileloadend', () => {
                resolve('tileloadend');
            });
        });

        try {
            const result = await Promise.race([loadPromise, timeoutPromise]);
            if (result === 'tileloadend') {
                return 'success';
            }
        } catch (error) {
            map.removeLayer(xyzLayer);
            this.updateMap();
            
            return error;
        }
    }
    @bind
    addGoogleLayer({ apiKey, addToBaseLayers, mapType = 'satellite' }) {
        if (!apiKey) {
            console.error('Google API Key is required!');
            return;
        }
    
        // Remove any existing Google layer
        const existingGoogleLayer = this.getMap()
            .getLayers()
            .getArray()
            .find(layer => layer.get('title') === 'Google Maps');
    
        if (existingGoogleLayer) {
            this.getMap().removeLayer(existingGoogleLayer);
        }
    
        // Create new Google Maps layer
        const googleLayer = new TileLayer({
            title: 'Google Maps',
            source: new Google({
                key: apiKey,
                scale: 'scaleFactor2x',
                highDpi: true,
                mapType,
            }),
        });
    
        // Add to Base Layers or directly to Map
        if (addToBaseLayers) {
            const baseLayersGroup = this.getMap()
                .getLayers()
                .getArray()
                .find(layer => layer.get('title') === 'baseLayers');
    
            if (baseLayersGroup) {
                baseLayersGroup.getLayers().push(googleLayer);
            } else {
                console.error('Base Layers Group not found!');
            }
        } else {
            this.getMap().addLayer(googleLayer);
        }

        // Add the Google logo to the map
        this.getMap().addControl(new GoogleLogoControl());
        this.updateMap();
    }

    @bind
    updateMap() {
        // Cancel any pending render operation
        if (this._renderAnimationFrame) {
            cancelAnimationFrame(this._renderAnimationFrame);
        }
        
        // Schedule new render operation
        this._renderAnimationFrame = requestAnimationFrame(() => {
            this.getMap().updateSize && this.getMap().render();
            this._renderAnimationFrame = null;
        });
    }

    @bind
    getDefaultPreferences() {
        return this.defaultPreferences;
    }

    @bind
    loadPreferences(preferences) {
        if (preferences) {
            const { baseLayers, allLayers } = preferences;
            this.getMap()
                .getLayers()
                .array_.forEach(layerGroup => {
                    if (layerGroup.values_.title === 'baseLayers' && baseLayers) {
                        layerGroup.getLayers().array_.forEach((layer, index) => {
                            const visible = get(baseLayers, `layers[${index}].visible`);
                            layer.values_.visible = isDefined(visible) ? visible : index === 0;
                            layer.values_.opacity = this.setOpacity(get(baseLayers, `layers[${index}].opacity`));
                        });
                    } else if (layerGroup.values_.title === 'allLayers' && allLayers) {
                        this.orderLayers(layerGroup.getLayers().array_, allLayers.layers);
                    }
                });
            this.getMap().render();
        }
    }

    @bind
    orderLayers(array, order) {
        if (array && order && order.length) {
            array.sort((a, b) => order.findIndex(item => item.title === a.values_.title) - order.findIndex(item => item.title === b.values_.title));
        }
        this.getMap().render();
    }

    @bind
    setPinCenter(longitude, latitude, accuracy = 20, userLocation = false) {
        const long = parseFloat(longitude);
        const lat = parseFloat(latitude);

        this.IamHereLayer = this.getMap()
            .getLayers()
            .array_.find(layer => layer.values_.title === 'IamHere Layer');

        if (this.IamHereLayer) {
            this.IamHereLayer.getSource().clear();
            this.map.removeLayer(this.IamHereLayer);
            this.IamHereLayer = null;
        }

        const marker = new Feature({
            geometry: new Point(fromLonLat([long, lat])),
        });

        if (userLocation) {
            const minAccuracy = 200;
            const radius = accuracy < minAccuracy ? accuracy : minAccuracy;

            const vectorSource = new VectorSource({
                features: [marker],
            });
            const userLocationZIndex = this.getNextLayerZIndex() + 2000;
            
            this.IamHereLayer = new VectorLayer({
                source: vectorSource,
                style: (feature, resolution) => {
                    const scaledRadius = (radius * 0.6) / resolution;
                    return new Style({
                        image: new CircleStyle({
                            radius: scaledRadius,
                            fill: new Fill({
                                color: 'rgba(0, 81, 255, 0.3)',
                            }),
                        }),
                        text: new Text({
                            font: 'normal normal normal 24px/4 "Material Design Icons"',
                            text: String.fromCodePoint(mdiMapping['map-marker'], 16),
                            fill: new Fill({ color: '#3D64B7' }),
                        }),
                    });
                },
                title: 'IamHere Layer',
                zIndex: userLocationZIndex,
            });
            this.getMap().addLayer(this.IamHereLayer);
            
            // Apply the z-index to ensure it's properly set
            applyLayerZIndex(this.IamHereLayer, userLocationZIndex);
        }

        this.flyTo(marker, accuracy);
    }

    @bind
    flyTo(marker, accuracy, isDrawing) {
        const duration = 2500;
        let zoom = this.getView().getZoom();
        const currentZoom = 4;
        if (zoom > 4) {
            zoom = currentZoom;
        }
        let parts = 2;
        let called = false;

        const onComplete = complete => {
            --parts;
            if (called) {
                return;
            }
            if (parts === 0 || !complete) {
                called = true;
            }
        };

        this.getView().animate(
            {
                center: marker.getGeometry().getCoordinates(),
                duration: duration,
            },
            onComplete
        );
        this.getView().animate(
            {
                zoom: zoom - 1,
                duration: duration / 2,
            },
            {
                zoom: isDrawing ? 7 : 18,
                duration: duration / 2,
            },
            onComplete
        );
    }

    @bind
    addCustomInteractions(type, drawingType, callback) {
        this.removeAllCustomInteractions();
        this.customInteractions[type].addInteractionOnMap(drawingType, callback);
    }

    @bind
    removeAllCustomInteractions() {
        try {
            for (const key in this.customInteractions) {
                this.customInteractions[key].removeInteractionFromMap();
            }
        } catch (e) {}
    }

    @bind
    getLastInteraction() {
        return this.lastInteractionType;
    }

    @bind
    updateCesiumImageryLayer(item, updateFn) {
        if (!this.enable3D || !this._ol3d) return;

        if (!(item instanceof TileLayer && item.getSource() instanceof TileWMS)) return;

        const scene = this._ol3d.getCesiumScene();
        const layerTitle = item.get('title');

        if (!scene?.imageryLayers) return;

        for (let i = 0; i < scene.imageryLayers.length; i++) {
            const layer = scene.imageryLayers.get(i);
            if (
                layer._layerTitle === layerTitle ||
            layer._originalLayer === item ||
            layer._wmsSourceLayer === item
            ) {
                updateFn(layer); 
                scene.requestRender();
                break;
            }
        }
    }

    @bind
    toggleMapLayer(item, isBaseLayer, visible = null, threeDVisible) {
        if (isBaseLayer) {
            // base layer handling
            const baseLayers = this.getLayersGroup('baseLayers');
            baseLayers.getLayers().array_.forEach(layer => {
                layer.values_.visible = layer.values_.title === get(item, 'values_.title');
            });
            if (this.enable3D) {
                this.setCesiumBaseLayer(this.cesiumBaseLayers[item.values_.title]);
            }
        } else if (this.enable3D) {
            // WMS - 3D layer handling
            if (item instanceof TileLayer && item.getSource() instanceof TileWMS) {
                const newVisibility = visible !== null ? visible : !item.getVisible();
                item.setVisible(newVisibility);
                
                this.updateCesiumImageryLayer(item, (layer) => {
                    layer.show = newVisibility;
                });

            } else {
                // entity layer handling in 3D
                const { name } = item.values_;
                const entityLayer = this.getEntityLayer(name);
                if (name) {
                    const cesiumScene = this._ol3d.getCesiumScene();
                    const primitives = cesiumScene?.primitives;
                    for (let i = 0; i < primitives?.length; i++) {
                        const primitive = primitives.get(i);
                        if (primitive && primitive instanceof Cesium?.Cesium3DTileset) {
                            if (primitive.featureIdLabel === name) {
                                primitive.show = threeDVisible;
                            }
                        }
                    }
                    if (entityLayer?.dataSource3D) {
                        const newVisibility = visible !== null ? visible : !entityLayer.dataSource3D.show;
                        entityLayer.dataSource3D.show = newVisibility;
                        entityLayer.getLayer().setProperties({ visible3d: newVisibility });
                        entityLayer.setVisibility(newVisibility);
                    }
                    if (visible) {
                        entityLayer?.refresh3DMapLayers(this.getCesiumObj(), this.getOl3d(), this.enable3D);
                    }
                }
            }
        } else {
            // 2D mode layer handling
            const { title } = item.values_;
            item.setVisible(visible !== null ? visible : !item.getVisible());
            
            if (isDefined(this.lastInteractionType)) {
                if (visible) {
                    if (title === 'Measure Layer') {
                        this.customInteractions['measurement'].hideMeasureOverlays();
                        return this.addCustomInteractions('measurement', -1);
                    }
                    return this.removeAllCustomInteractions();
                }
                if (title === 'Measure Layer') {
                    this.customInteractions['measurement'].showMeasureOverlays();
                }
                if (this.lastInteractionType[1] !== 100) {
                    this.addCustomInteractions(this.lastInteractionType[0], this.lastInteractionType[1]);
                }
            }
        }
        
        this.getMap().render();
    }

    @bind
    refreshMapLayers(is3d) {
        // Batch all refresh operations to avoid multiple renders
        let needsRender = false;
        
        Object.keys(this.entityLayers).forEach(key => {
            const layer = this.entityLayers[key];
            if (layer && !layer.isFeatureLoaded) {
                layer.isFeatureLoaded = false;
                
                if (layer instanceof ReplayLayer) {
                    layer.setLayer();
                    needsRender = true;
                }
            }
        });

        const mapLayers = this.getMap().getLayers();
        (mapLayers || []).forEach(layer => {
            if (layer instanceof LayerGroup) {
                const layers = layer.getLayers();
                (layers || []).forEach(lyr => {
                    if (lyr?.values_?.attributes?.type !== 'drawing-layer') {
                        if (lyr instanceof VectorLayer && lyr.getVisible()) {
                            // Only refresh visible layers to improve performance
                            this.refreshLayer(lyr);
                            needsRender = true;
                        }
                    }
                });
            }
        });
        
        if (!is3d) this._ol3d = null;
        
        // Only render once at the end if changes were made
        if (needsRender) {
            this.updateMap();
        }
    }

    @bind
    refreshLayer(lyr) {
        try {
            if (lyr instanceof VectorLayer) {
                const entityLayer = this.entityLayers[lyr?.values_.attributes?.id];
                const relatedEntities = entityLayer?.relatedEntities.length && entityLayer.relatedEntities[0];
                const relatedLayer = relatedEntities && relatedEntities.getLayer();
                const isHeatmap = entityLayer?.heatMapLayers?.[0];
                const heatmapLayer = isHeatmap?.getLayer();
                
                // Batch all source refreshes
                heatmapLayer && this.refreshLayerSource(heatmapLayer);
                this.refreshLayerSource(lyr);
                this.refreshLayerSource(entityLayer.layer);
                relatedLayer && this.refreshLayerSource(relatedLayer);
            }
        } catch (e) {}
    }

    @bind
    refreshLayerSource(layer) {
        if (!layer) return;
        const source = layer.getSource();
        if (source) {
            source.refresh();
            if (source?.getSource) {
                const innerSource = source.getSource();
                if (innerSource) {
                    innerSource.refresh();
                }
            }
        }
    }

    @bind
    @memoize()
    hasFeature(item) {
        if (item.getSource() instanceof TileLayer || item.getSource() instanceof TileWMS) {
            return false;
        } else {
            const extent = item.getSource().getExtent();
            return !isNaN(extent[0]);
        }
    }

    @bind
    async zoomToMapLayer(item, zoom) {
        if (item?.getSource() instanceof TileLayer || item?.getSource() instanceof TileWMS) {
            return;
        }
        const extent = await item?.getSource().getExtent();
        if (!isNaN(parseInt(extent?.[0]))) {
            // Because someties array does have Infinity
            const size = await this.getMap().getSize();
            const mapView = await this.getMap().getView();
            await mapView.fit(extent, { size, maxZoom: zoom ? zoom : 16 });
        }
    }

    @bind
    @memoize()
    findLayer(layerGroupName, layerTitle) {
        const layersGroup = this.getLayersGroup(layerGroupName);
        return layersGroup && layersGroup.getLayers ? layersGroup.getLayers().array_.find(({ values_ }) => values_.title === layerTitle) : layersGroup;
    }

    @bind
    findLayerById(id) {
        const layersGroup = this.getLayersGroup('allLayers');
        return layersGroup && layersGroup.getLayers ? layersGroup.getLayers().array_.find(({ values_ }) => values_?.attributes?.id === id) : null;
    }
    
    @bind
    findLayerByTitle(title) {
        const layersGroup = this.getLayersGroup('allLayers');
        return layersGroup && layersGroup.getLayers ? layersGroup.getLayers().array_.find(({ values_ }) => values_.title === title) : null;
    }
    
    @bind
    findLayerByType(id, type) {
        const layers = this.map?.getLayers()?.array_;
        if (!layers?.length) return null;
        return layers.find(lyr => {
            const atr = lyr.values_?.attributes || {};
            return atr?.id === id && atr?.type === type;
        });
    }

    @bind
    removeGoogleLayer() {
        try {
            const map = this.getMap();
            const mapContainer = map.getTargetElement();

            if (!mapContainer) return;
            const googleLayer = map.getLayers()
                .getArray()
                .find(layer => layer.get('title') === 'Google Maps');

            if (googleLayer) {
                map.removeLayer(googleLayer);
            }
            const controls = map.getControls().getArray();
            const googleControls = controls.filter(control => 
                control instanceof GoogleLogoControl || 
                control.get('title') === 'GoogleLogoControl'
            );
            googleControls.forEach(control => {
                map.removeControl(control);
            });
            const googleElems = mapContainer.querySelectorAll('.gm-style, .olgm-control, .olgm-map, [src*="google_on_white"]');
            googleElems.forEach(el => {
                el.remove();
            });
            const googleLogos = mapContainer.querySelectorAll('img[src*="google_on_white"]');
            googleLogos.forEach(logo => {
                logo.remove();
            });
            this.updateMap();
        } catch (err) {}
    }


    @bind
    removeLayerByPredicate(predicate, notFoundMessage) {
        const allLayersGroup = this.getLayersGroup('allLayers');

        if (!allLayersGroup) {
            console.warn('All layers group not found!');
            return false;
        }

        const allLayers = allLayersGroup.getLayers();
        const allLayersArray = allLayers.getArray();

        const layerIndex = allLayersArray.findIndex(predicate);

        if (layerIndex === -1) {
            console.warn(notFoundMessage);
            return false;
        }

        allLayersArray.splice(layerIndex, 1);
        allLayers.changed();
        this.updateMap();

        return true;
    }

    @bind
    removeLayerFromAllLayersById(layerId) {
        const result = this.removeLayerByPredicate(
            layer => layer.get('id') === layerId || 
                 (layer.get('attributes') && layer.get('attributes').id === layerId),
        `Layer with ID "${layerId}" not found in all layers!`
        );
        
        if (result) {
            this.recalculateAllLayerZIndexes();
        }
        
        return result;
    }
    
    /**
     * Get the next available z-index for a new non-base layer.
     * This ensures new layers are added on top with proper z-index increments.
     * It iterates over the managed entityLayers to find the max z-index.
     *
     * @returns {number} The next available z-index value.
     */
    @bind
    getNextLayerZIndex() {
        const entityLayers = Object.values(this.entityLayers);
        if (!entityLayers?.length) {
            return 5; 
        }

        let maxZIndex = 0;
        entityLayers.forEach(layer => {
            if (layer && typeof layer.zIndex === 'number') {
                const zIndex = layer.zIndex;
                if (zIndex > maxZIndex) {
                    maxZIndex = zIndex;
                }
            }
        });

        return maxZIndex + 5;
    }
    
    reorderLayersByIdList(layerIdOrder) {

        const group = this.getLayersGroup('allLayers');
        if (!group || !Array.isArray(layerIdOrder)) return;
        const olLayers = group.getLayers().getArray();

        const idToLayer = {};
        olLayers.forEach(layer => {
            const id = layer.get('attributes')?.id;
            if (id) idToLayer[id] = layer;
        });
        
        const uniqueLayerIdOrder = Array.from(new Set(layerIdOrder));
        const newLayers = [...uniqueLayerIdOrder].map(id => idToLayer[id]).filter(Boolean);
        
        try {
            const newOlCollection = new Collection(newLayers);
            group.setLayers(newOlCollection);
            
        } catch (e) {
            console.error('Dragg layer error', e);
        }
        this.recalculateAllLayerZIndexes();
        
        if (this.getMap && this.getMap()) {
            this.getMap().render();
        }
    }

    @bind
    recalculateAllLayerZIndexes() {
        const layersGroup = this.getLayersGroup('allLayers');
        if (!layersGroup) return;
        
        const layers = layersGroup.getLayers().getArray();
        
        const baseLayers = [];
        const nonBaseLayers = [];
        
        layers.forEach(layer => {
            if (!layer) return;
            if (layer.get('baseLayer') === true) {
                baseLayers.push(layer);
            } else {
                nonBaseLayers.push(layer);
            }
        });
        
        baseLayers.forEach(layer => {
            if (!layer) return;
            applyLayerZIndex(layer, 1);
        });
        
        const layersToProcess = [...nonBaseLayers];
        const totalLayersToProcess = layersToProcess.length;
        
        // Batch all layer updates before rendering
        const updates = [];
        
        layersToProcess.forEach((layer, index) => {
            if (!layer) return;
            
            const layerType = layer.get('attributes')?.type || 'unknown';
            const position = totalLayersToProcess - index - 1;
            
            const baseZIndex = (layerType === 'entity' || layerType === 'event') 
                ? calculateLayerZIndex(position, layerType) 
                : calculateLayerZIndex(position);
                
            const isVisible = layer.getVisible();
            
            // Store updates instead of applying immediately
            updates.push({ layer, layerType, baseZIndex, isVisible });
        });
        
        // Apply all updates in batch
        updates.forEach(({ layer, layerType, baseZIndex, isVisible }) => {
            if (layerType !== 'entity' && layerType !== 'event') {
                applyLayerZIndex(layer, baseZIndex);
                layer.setVisible(isVisible);
            } else {
                const actualLayerZIndex = calculateSubLayerZIndex(baseZIndex, 'entity');
                applyLayerZIndex(layer, actualLayerZIndex);
                layer.setVisible(isVisible);
            }
            
            const layerId = layer.get('attributes')?.id;
            let entityLayer = this.entityLayers[layerId];
            
            if (!entityLayer && (layerType === 'entity' || layerType === 'event')) {
                Object.keys(this.entityLayers).forEach(key => {
                    const candidate = this.entityLayers[key];
                    if (candidate?.info?.id === layerId || candidate?.layerId === layerId) {
                        entityLayer = candidate;
                    }
                });
            }
            
            if (entityLayer && typeof entityLayer.updateLayer === 'function' && 
                (layerType === 'entity' || layerType === 'event' || layerType === 'drawing-layer' ||
                    layerType === 'drawing' || entityLayer.constructor?.name === 'ClusterLayer')) {
                entityLayer.zIndex = baseZIndex; 
                entityLayer.updateLayer({ zIndex: baseZIndex, visible: isVisible });
            }
            
            if ((layerType === 'drawing' || layerType === 'drawing-layer') && this.selectedLayer && this.selectedLayer.id === layer.get('attributes')?.id) {
                if (this.customInteractions && this.customInteractions.drawing) {
                    if (this.customInteractions.drawing.drawTypeIndex !== undefined) {
                        this.customInteractions.drawing.initDrawTool(this.customInteractions.drawing.drawTypeIndex);
                    }
                }
            }
        });
        
        this.updateMap();
    }

    @bind
    removeLayerFromBaseLayersByName(layerName) {
        return this.removeLayerByPredicate(
            layer => layer.get('title') === layerName || 
                 layer.get('name') === layerName,
        `Layer "${layerName}" not found in base layers!`
        );
    }

    @bind
    onOpacityChange(item, value) {
        
        item.setOpacity(value);
        this.getMap().render();

        this.updateCesiumImageryLayer(item, (layer) => {
            layer.alpha = value;
        });
    }

    @memoize()
    createCircleOutOverlay(position, count, selectedLayer) {
        const elem = document.createElement('div');
        if (selectedLayer) elem.setAttribute('class', 'pulse-static');
        else elem.setAttribute('class', 'pulse');
        const size = parseInt(38 + count / 200);
        elem.style.width = elem.style.height = `${size}px`;

        return new Overlay({
            id: 'hoverOverlay',
            title: 'hoverOverlay',
            element: elem,
            position: position,
            positioning: 'center-center',
            stopEvent: false,
        });
    }

    @bind
    closeOverlay(overlay) {
        if (overlay instanceof Overlay) {
            overlay.setPosition(undefined);
        }
    }

    @bind
    createOverlay(element) {
        return new Overlay({
            element: element,
            autoPan: true,
            autoPanAnimation: {
                duration: 250,
            },
        });
    }

    @bind
    findHighLightLayer() {
        const allLayers = this.getMap()?.getLayers()?.array_;
        return allLayers.find(lyr => !(lyr instanceof LayerGroup) && lyr.values_?.attributes?.id === '9b1caeb6-243f-11ed-8f33-0242ac130009');
    }

    @bind
    async highlightFeature(coord, count = 1, selectedLayer) {
        const overlay = this.createCircleOutOverlay(coord, count, selectedLayer);
        this.overlayMap.push(overlay);
        this.getMap() && this.getMap().addOverlay(overlay);
    }

    @bind
    highLightFeatures(layerId, isVisible) {
        try {
            if (!this.is3dEnabled()) {
                const lyr = this.findHighLightLayer();
                if (lyr) {
                    lyr.getSource().clear();
                }
            
                const layersGroup = this.getMap()
                    .getLayers()
                    .array_.find(layer => layer.values_.title === 'allLayers');
                
                if (!layersGroup) {
                    return;
                }
            
                const layer = layersGroup.getLayers().array_.find(layer => 
                    layer.values_.attributes.id === layerId);
                
                if (!layer) {
                    return;
                }
            
                if (!layer.getVisible() && !isVisible) {
                    return;
                }
            
                const layerFeatures = layer.getSource?.().getFeatures();
                if (layerFeatures && layerFeatures.length && lyr) {
                    lyr.getSource().addFeatures(layerFeatures);
                }
            
                this.map.render();
            }
        } catch (error) {
            console.error('Error in highLightFeatures:', error.message);
        }
    }

    @bind
    @memoize()
    getLayersGroup(title) {
        const layers = this.getMap && this.getMap().getLayers && this.getMap().getLayers();

        if (layers && title) {
            return layers.array_.find(layerGroup => layerGroup.values_.title === title);
        }
        return null;
    }

    @bind
    setMap(targetId, controls, title) {
        this.setView();
        this.map = new Map({
            controls: defaultControls({ attribution: false }).extend(controls),
            target: targetId,
            view: this.view,
        });
        const allBaseLayers = [this.getOSMLayer(false)];
       
        const olBaseLayers = new LayerGroup({
            title: 'baseLayers',
            layers: allBaseLayers,
            zIndex: 0
        });

        const olLayers = new LayerGroup({
            title: 'allLayers',
            layers: [],
            zIndex: 5
        });

        const olRelatedLayers = new LayerGroup({
            title: 'allRelatedLayers',
            layers: [],
        });

        for (const lyr of allBaseLayers) {
            applyLayerZIndex(lyr, 1);
            const baseLayer = get(lyr, 'values_.title');
            if (title === baseLayer || title === 'locationMap') {
                lyr.setVisible(true);
                break;
            }
        }
        this.map.addLayer(olBaseLayers);
        this.map.addLayer(olLayers);
        this.map.addLayer(olRelatedLayers);
    }

    @bind
    getMap() {
        return this.map;
    }

    @bind
    setView() {
        this.view = new View({
            projection: 'EPSG:3857',
            center: fromLonLat([0, 0]),
            zoom: 4,
            maxZoom: 21,
            minZoom: 2,
        });
    }

    @bind
    getView() {
        return this.view;
    }

    @bind
    addLayer(layer) {
        this.map.addLayer(layer);
    }

    @bind
    async getLayerType(info) {
        const { styles } = info || {};
        const style = styles?.pinStyle;
        
        switch ((style || '').toLowerCase()) {
            case 'cluster':
                return new ClusterLayer(info, this);
            case 'related':
                return new RelatedEntityLayer(info.parentLayer, info.entities, info, this);
            case 'heatmap':
                return new HeatMapLayer(info, this, info.parentLayer);
            case 'drawing':
                const drawingLayer = await new DrawingLayer(info, this);
                return drawingLayer;
            case cesiumLayerConfig?.cesiumLayer?.type:
            case cesiumLayerConfig?.cesiumLayer?.oldType:
                return new ThreeDLayer(info, this);
            case 'replay':
                const replayLayer = await new ReplayLayer(info, this);
                return replayLayer;
            default:
                return new EntityLayer(info, this);
        }
    }

    @bind
    async updateEntity(id, attributes) {
        const { primary } = attributes || {};
        let zIndex = attributes?.zIndex;
        const { heatmap, related_entities } = primary || {};

        const layersGroup = this.getLayersGroup('allLayers');
        const currentLayers = layersGroup ? layersGroup.getLayers().getArray() : [];
        const currentLayerCount = currentLayers.length - 1;

        const isHeatmapEnabled = heatmap?.dataVisualisation;
        const isRelatedEntitiesEnabled = related_entities?.relatedEntities || related_entities?.showCounters;

        // Find the existing entity layer using multiple possible keys
        let entityLayer = this.entityLayers[id];
        if (!entityLayer) {
            entityLayer = Object.values(this.entityLayers).find(layer => 
                layer?.layerId === id || layer?.info?.id === id
            );
        }
        
        const wasSelected = isLayerSelected(entityLayer?.layer);
        
        const originalZIndex = entityLayer?.originalZIndex || entityLayer?.zIndex;
        
        if (zIndex === undefined) {
            zIndex = originalZIndex;
            if (zIndex === undefined) {
                const layerPosition = currentLayerCount;
                zIndex = calculateLayerZIndex(layerPosition);
            }
        }

        this.removeHeatmapLayer(id);
    
        if (entityLayer?.relatedEntities?.length) {
            this.removeRelatedmapLayer(id);
            entityLayer.relatedEntities = [];
            this.isRelatedMap = this.isRelatedMap.filter(layerId => layerId !== entityLayer?.info?.id);
        }

        // Create a new layer info object that includes the original z-index
        const layerInfo = {
            ...primary,
            zIndex: typeof originalZIndex === 'number' ? originalZIndex : zIndex,
            originalZIndex: originalZIndex
        };
        
        this.entityLayers[id] = await this.getLayerType(layerInfo);
        entityLayer = this.entityLayers[id];
        
        // Restore the selection state on the new layer if it was previously selected
        if (wasSelected && entityLayer?.layer) {
            entityLayer.layer.setProperties({ selectedlayer: true });
        }
        
        // Also store using the layer's internal ID to ensure consistency
        if (entityLayer.layerId && entityLayer.layerId !== id) {
            this.entityLayers[entityLayer.layerId] = entityLayer;
        }
        if (entityLayer.info?.id && entityLayer.info?.id !== id) {
            this.entityLayers[entityLayer.info?.id] = entityLayer;
        }
        
        if (zIndex !== undefined) {
            // Update the layer with the z-index (preserved or explicitly provided)
            entityLayer.updateLayer({ zIndex });
            
            // Preserve the original z-index if available
            if (typeof entityLayer.preserveOriginalZIndex === 'function' && 
                typeof originalZIndex === 'number') {
                entityLayer.preserveOriginalZIndex(originalZIndex);
            } else if (typeof originalZIndex === 'number') {
                // Fallback: store it manually if the method doesn't exist
                entityLayer.originalZIndex = originalZIndex;
            }
        }

        if (isHeatmapEnabled) {
            const layerId = `${entityLayer.info?.id}-heatmap`;
            const actualParentZIndex = originalZIndex || entityLayer.zIndex;
            const heatmapZIndex = calculateSubLayerZIndex(actualParentZIndex, 'heatmap');
            
            const heatmapInfo = {
                id: layerId,
                ...primary,
                styles: { ...primary.styles, pinStyle: 'heatMap' },
                parentLayer: entityLayer,
                zIndex: heatmapZIndex,
                type: entityLayer.info?.type
            };
        
            const heatmapLayer = await this.getLayerType(heatmapInfo);
            
            if (typeof heatmapLayer.storeParentOriginalZIndex === 'function' && 
                typeof originalZIndex === 'number') {
                heatmapLayer.storeParentOriginalZIndex(originalZIndex);
            }
            
            entityLayer.heatMapLayers = [heatmapLayer];
            this.isHeatMap.push(entityLayer.info?.id);
        }

        if (isRelatedEntitiesEnabled) {
            const layerId = `${entityLayer.info?.id}-r0`;
            const actualParentZIndex = originalZIndex || entityLayer.zIndex;
            const relatedZIndex = calculateSubLayerZIndex(actualParentZIndex, 'related_sub_entity');
            
            const relatedInfo = {
                id: layerId,
                primary,
                parentLayer: entityLayer,
                styles: { ...primary.styles, pinStyle: 'related' },
                related_entities: primary.related_entities,
                visible: primary.visible,
                zIndex: relatedZIndex,
                type: entityLayer.info?.type
            };
        
            const relatedLayer = await this.getLayerType(relatedInfo);
            
            if (typeof relatedLayer.storeParentOriginalZIndex === 'function' && 
                typeof originalZIndex === 'number') {
                relatedLayer.storeParentOriginalZIndex(originalZIndex);
            }
            
            entityLayer.relatedEntities = [relatedLayer];
            this.isRelatedMap.push(entityLayer.info?.id);
        }
       
        this.getMap().render();
        this.updateMap();
    }

    @bind
    removeHeatmapLayer(id) {
        const findHeatmapLayer = this.map?.getLayers()?.array_?.find(lyr => {
            return lyr instanceof Heatmap && lyr?.values_?.attributes?.id?.split('-heatmap')?.[0] === id;
        });
        findHeatmapLayer && this.map?.removeLayer(findHeatmapLayer);
    }

    @bind
    removeRelatedmapLayer(id) {
        this.map?.getLayers()?.array_?.find(lyr => {
            if (lyr?.values_?.attributes?.id?.split('-r0')?.[0] === id) {
                this.map?.removeLayer(lyr);
            }
        });
    }

    @bind
    async addEntityLayer(info) {
        const { attributes } = info || {};
        const layersGroup = this.getLayersGroup('allLayers');
        const currentLayers = layersGroup ? layersGroup.getLayers().getArray() : [];
        const currentLayerCount = currentLayers.length;
        
        let zIndex = info?.zIndex || attributes?.zIndex;

        if (!zIndex) {
            const layerPosition = currentLayerCount;
            zIndex = calculateLayerZIndex(layerPosition);
        }

        info.zIndex = zIndex;

        const entityLayer = await this.getLayerType(info);
        this.entityLayers[entityLayer.layerId || entityLayer?.info?.id] = entityLayer;
        
        const { heatmap, related_entities } = info;
        const isHeatmapEnabled = heatmap?.dataVisualisation;
        const isRelatedEnabled = related_entities?.relatedEntities || related_entities?.showCounters;

        if (isHeatmapEnabled) {
            const heatmapLayerId = `${entityLayer.info?.id}-heatmap`;
            const heatmapZIndex = calculateSubLayerZIndex(entityLayer.zIndex, 'heatmap');

            const heatmapInfo = {
                id: heatmapLayerId,
                ...info,
                styles: { ...info.styles, pinStyle: 'heatMap' },
                parentLayer: entityLayer,
                zIndex: heatmapZIndex,
                type: entityLayer.info?.type
            };
            
            try {
                const heatmapLayer = await this.getLayerType(heatmapInfo);
                
                if (typeof heatmapLayer.storeParentOriginalZIndex === 'function') {
                    heatmapLayer.storeParentOriginalZIndex(entityLayer.zIndex);
                }
                
                entityLayer.heatMapLayers = [heatmapLayer];
                this.isHeatMap.push(entityLayer.info?.id);
            } catch (error) {
                console.error('olMapController=addEntityLayer heatmap creation failed:', error);
            }
        }

        if (isRelatedEnabled) {
            const relatedLayerId = `${entityLayer.info?.id}-r0`;
            const relatedZIndex = calculateSubLayerZIndex(entityLayer.zIndex, 'related_sub_entity');
            
            const relatedInfo = {
                id: relatedLayerId,
                ...info,
                parentLayer: entityLayer,
                styles: { ...info.styles, pinStyle: 'related' },
                related_entities: info.related_entities,
                related_entities_th: info.related_entities_th, // Explicitly copy this property
                visible: info.visible,
                zIndex: relatedZIndex,
                type: entityLayer.info?.type
            };
            
            const relatedLayer = await this.getLayerType(relatedInfo);
            
            if (typeof relatedLayer.storeParentOriginalZIndex === 'function') {
                relatedLayer.storeParentOriginalZIndex(entityLayer.zIndex);
            }
            
            entityLayer.relatedEntities = [relatedLayer];
            this.isRelatedMap.push(entityLayer.info?.id);
        }
       
        this.getMap().render();
        this.updateMap();
    
        return entityLayer.layerId;
    }

    @bind
    getEntityLayer(id) {
        return this.entityLayers[id];
    }

    @bind
    @memoize()
    getOverlayLayer(key) {
        return this.overlayLayers[key];
    }


    @bind
    removeMapLayer(layer, entityLayer) {
        const src = layer?.getSource();
        src && src.clear();
        this.getMap().removeLayer(layer);
    }

    @bind
    getOSMLayer(isVisible) {
        return new TileLayer({
            title: 'OSM',
            preload: 4,
            baseLayer: true,
            visible: isVisible,
            source: new OSM(),
        });
    }

    @bind
    removeIamHereOverlay() {
        if (this.IamHereLayer) {
            this.IamHereLayer.getSource().clear();
            this.map.removeLayer(this.IamHereLayer);
            this.IamHereLayer = null;
        }
    }

    cesiumBaseLayers = {
        OSM: 4,
    };

    @bind
    setCesiumBaseLayer(selectedIdx) {
        (get(this._ol3d, 'globe_._imageryLayerCollection._layers') || []).forEach((lyr, idx) => {
            if (idx > 0 && idx !== selectedIdx) {
                lyr.show = 0;
                return;
            }
            lyr.show = 1;
        });
    }

    @bind
    setOLCesium(showToastr) {
        try {
            this.removeHightLightFeatures();
            this._ol3d = new OLCesium({
                map: this.map,
                sceneOptions: {
                    mapProjection: new Cesium.WebMercatorProjection(),
                },
            });
            const selectedBaseLayer = this.getLayersGroup('baseLayers')
                .getLayers()
                .array_.find(l => l.values_.visible);
            
            const baseLayerValues = this.getSelectedBaseLayer().values_;
            const layerTitle = selectedBaseLayer.values_.title;
        
            if (layerTitle in this.cesiumBaseLayers) {
                this.setCesiumBaseLayer(this.cesiumBaseLayers[layerTitle]);
            } else {
                changeBaseLayer(this, baseLayerValues);
            }
        } catch (e) {
            showToastr({ severity: 'error', detail: 'Something went wrong. Map is refreshing.' });
            setTimeout(() => {
                window.location.reload(true);
            }, 3000);
            // eslint-disable-next-line no-console
            console.error(e);
        }
    }

    @bind
    checkWebglSupport() {
        try {
            var canvas = document.createElement('canvas');
            return !!window.WebGLRenderingContext && (canvas.getContext('webgl') || canvas.getContext('experimental-webgl'));
        } catch (e) {
            return false;
        }
    }

    @bind
    setupWmsLayersFor3D() {
        if (!this._ol3d) return;
    
        const scene = this._ol3d.getCesiumScene();
        if (!scene) return;

        const allWmsLayers = [];

        this.getMap().getLayers().forEach(layerGroup => {
            if (layerGroup instanceof LayerGroup) {
                layerGroup.getLayers().forEach(layer => {
                    if (layer && 
                    layer.getVisible() && 
                    ((layer.get('attributes') && layer.get('attributes').type === 'wms') || 
                     (layer.getSource() instanceof TileWMS))) {
                    
                        allWmsLayers.push(layer);
                    }
                });
            }
        });

        allWmsLayers.forEach(wmsLayer => {
            const wmsSource = wmsLayer.getSource();
            if (!wmsSource) return;
        
            const urls = wmsSource.getUrls ? wmsSource.getUrls() : null;
            const url = urls && urls.length > 0 ? urls[0] : wmsSource.getUrl ? wmsSource.getUrl() : null;
            const params = wmsSource.getParams ? wmsSource.getParams() : null;
            const layerNames = params?.LAYERS;
            const opacity = wmsLayer.getOpacity ? wmsLayer.getOpacity() : 1;
            const layerTitle = wmsLayer.get('title');
        
            if (!url || !layerNames) return;
        
            let layerExists = false;
            for (let i = 0; i < scene.imageryLayers.length; i++) {
                const cesiumLayer = scene.imageryLayers.get(i);
                if (cesiumLayer._originalLayer === wmsLayer || 
                cesiumLayer._wmsSourceLayer === wmsLayer ||
                cesiumLayer._layerTitle === layerTitle) {
                
                    cesiumLayer.alpha = opacity;
                    cesiumLayer.show = wmsLayer.getVisible();
                    layerExists = true;
                    break;
                }
            }
        
            if (!layerExists) {
                try {
                    const wmsProvider = new Cesium.WebMapServiceImageryProvider({
                        url: url,
                        layers: layerNames,
                        parameters: {
                            ...params,
                            format: params.FORMAT || 'image/png',
                            transparent: true
                        }
                    });
                
                    const cesiumLayer = scene.imageryLayers.addImageryProvider(wmsProvider);
                    if (cesiumLayer) {
                        cesiumLayer.alpha = opacity;
                        cesiumLayer.show = wmsLayer.getVisible();
                    
                        cesiumLayer._originalLayer = wmsLayer;
                        cesiumLayer._wmsSourceLayer = wmsLayer;
                        cesiumLayer._layerTitle = layerTitle;
                    
                        if (!wmsLayer._visibilityListenerSetup) {
                            wmsLayer.on('propertychange', (event) => {
                                if (event.key === 'visible') {
                                    const newVisibility = event.target.getVisible();
                                            
                                    if (this._ol3d && this._ol3d.getEnabled()) {
                                        const cesiumScene = this._ol3d.getCesiumScene();
                                        if (cesiumScene) {
                                            for (let i = 0; i < cesiumScene.imageryLayers.length; i++) {
                                                const imgLayer = cesiumScene.imageryLayers.get(i);
                                                if (imgLayer._originalLayer === wmsLayer) {
                                                    imgLayer.show = newVisibility;
                                                    cesiumScene.requestRender();
                                                    break;
                                                }
                                            }
                                        }
                                    }
                                }
                            });
                            wmsLayer._visibilityListenerSetup = true;
                        }
                    }
                } catch (error) {
                    console.error('Error adding WMS layer to Cesium:', error);
                }
            }
        });

        scene.requestRender();
    }

    @bind
    toggleOLCesium(showToastr) {
        this.enable3D = !this.enable3D;
    
        /**
         * Run OLCesium if selected
         * @fix Cesium crashes because of the right-hand rule for MutliPolygon (Open layers works fine).
         */
        if (!this._ol3d && this.enable3D) {
            this.setOLCesium(showToastr);
        }
        
        if (this.enable3D) {
            const isWebGL = this.checkWebglSupport();
        
            if (isWebGL) {
                for (const id in this.entityLayers) {
                    const entityLayer = this.entityLayers[id];
                    if (typeof entityLayer.toggleTo3DStyling === 'function') {
                        entityLayer.toggleTo3DStyling(Cesium, this._ol3d, this.enable3D, this);
                    }
                }
            
                if (this._ol3d) {
                    this._ol3d.setEnabled(true);
                    const selectedBaseLayer = this.getSelectedBaseLayer();

                    if (selectedBaseLayer) {
                        const layerTitle = selectedBaseLayer.values_.title;
                        if (layerTitle in this.cesiumBaseLayers) {
                            this.setCesiumBaseLayer(this.cesiumBaseLayers[layerTitle]);
                        }
                    }
                
                    try {
                        this.setupWmsLayersFor3D();
                    } catch (error) {
                        console.error('Error in setupWmsLayersFor3D:', error);
                    }
                }
            } else {
                this.enable3D = false;
            }
        } else {
            for (const id in this.entityLayers) {
                const entityLayer = this.entityLayers[id];
                if (typeof entityLayer.toggleTo3DStyling === 'function') {
                    entityLayer.toggleTo3DStyling(Cesium, this._ol3d, this.enable3D, this);
                }
            }
        
            if (this._ol3d) {
                this._ol3d.setEnabled(false);
            }
        }
    }

    @bind
    convertGeoJsonToFeature(geoJsonFeatures) {
        const geoJson = new GeoJSON();
        return geoJson.readFeatures(geoJsonFeatures);
    }

    @bind
    convertFeatureToGeoJson(feature) {
        const geoJson = new GeoJSON();
        return geoJson.writeFeature(feature);
    }

    @bind
    convertFeaturesToGeoJson(features) {
        const geoJson = new GeoJSON();
        return geoJson.writeFeatures(features);
    }

    @bind
    convertFeaturesToGeoJsonFeatures(features) {
        return this.convertFeaturesToGeoJson(features);
    }

    @bind
    getLayerByTitle(layerName) {
        const layers = this.getMap().getLayers && this.getMap().getLayers();
        return get(layers, 'array_', []).find(VectorLayer => VectorLayer.values_.title === layerName);
    }

    @bind
    takeScreenShoot(width = -1, height = -1) {
        const map = this.getMap();
        const convertMapToImage = this.convertMapToImage;
        const resizeImage = this.resizeBase64Img;
        const downloadImage = mapCanvas => {
            if (navigator.msSaveBlob) {
                navigator.msSaveBlob(mapCanvas.msToBlob(), 'map.png');
            } else {
                const link = document.createElement('a');
                document.body.appendChild(link);
                link.href = mapCanvas.toDataURL();
                link.download = 'map.png';
                link.click();
                document.body.removeChild(link);
            }
        };
        map.once('rendercomplete', () => {
            const mapCanvas = document.createElement('canvas');
            const size = map.getSize();
            mapCanvas.width = size[0] > 700 ? 700 : size[0];
            mapCanvas.height = size[0] > 700 ? (700 * size[1]) / size[0] : size[1];
            convertMapToImage(mapCanvas);
            if (width > 0 && height > 0) {
                resizeImage(mapCanvas.toDataURL(), width, height).then(canvas => {
                    downloadImage(canvas);
                });
            } else {
                downloadImage(mapCanvas);
            }
        });
        map.renderSync();
    }

    @bind
    async convertMapToImage(mapCanvas) {
        const mapContext = mapCanvas.getContext('2d');
        const olCanvasArr = document.querySelectorAll('.ol-layer canvas');
        const canvasPromises = Array.from(olCanvasArr).map(canvas => {
            if (canvas.width > 0) {
                const opacity = canvas.parentNode.style.opacity;
                mapContext.globalAlpha = opacity === '' ? 1 : Number(opacity);
                const transform = canvas.style.transform;
                // Get the transform parameters from the style's transform matrix
                const matrix = transform
                    .match(/^matrix\(([^(]*)\)$/)[1]
                    .split(',')
                    .map(Number);
                // Apply the transform to the export map context
                CanvasRenderingContext2D.prototype.setTransform.apply(mapContext, matrix);
                return mapContext.drawImage(canvas, 0, 0, mapCanvas.width, mapCanvas.height);
            }
            return Promise.resolve();
        });
        await Promise.all(canvasPromises);
    }

    @bind
    resizeBase64Img(base64, newWidth, newHeight = null) {
        return new Promise((resolve, reject) => {
            const img = document.createElement('img');
            img.src = base64;
            img.onload = () => {
                const scaleX = parseFloat(newWidth) / parseFloat(img.width);
                const scaleY = parseFloat(newHeight) / parseFloat(img.height);
                const scale = scaleX >= scaleY ? scaleX : scaleY;
                const calcWidth = parseInt(img.width * scale);
                const calcHeight = parseInt(img.height * scale);
                const canvas = document.createElement('canvas');
                canvas.width = calcWidth;
                canvas.height = calcHeight;
                const context = canvas.getContext('2d');
                context.drawImage(img, 0, 0, calcWidth, calcHeight);
                resolve(canvas);
            };
        });
    }

    @bind
    zoomToSavedMapExtent(extent) {
        if (extent && extent.length) {
            this.getMap()
                .getView()
                .fit(extent);
        }
    }

    @bind
    async getMapView() {
        const map = this.getMap();
        const mapCanvas = document.createElement('canvas');
        mapCanvas.width = 200;
        mapCanvas.height = 200;
        await this.convertMapToImage(mapCanvas);
        const extent = map.getView().calculateExtent(map.getSize());
        return { extent: extent, thumbnail: mapCanvas.toDataURL('image/jpeg') };
    }

    @bind
    removePinFromAboutMap() {
        const aboutMapLayer = this.getMap()
            .getLayers()
            .array_.find(layer => layer.values_.title === MAP_MARKER_LAYER);
        aboutMapLayer && this.getMap().removeLayer(aboutMapLayer);
    }

    @bind
    toggleMovePins(value, callback) {
        if (value) {
            const aboutMapLayer = this.getMap()
                .getLayers()
                .array_.find(layer => layer.values_.title === MAP_MARKER_LAYER);
            if (aboutMapLayer) {
                const source = aboutMapLayer.getSource();
                const features = source.getFeatures();
                const addedFeature = features[0];

                addedFeature.on('change', () => callback(addedFeature.getGeometry().getCoordinates()), addedFeature);
                this.dragDropFeatures = new Modify({
                    features: new Collection(features),
                });
                this.map.addInteraction(this.dragDropFeatures);
            }
            return;
        }

        this.dragDropFeatures && this.map.removeInteraction(this.dragDropFeatures);
    }

    @bind
    makeFeature(props, long, lat) {
        const { iconName, iconColor, svg, svgGraphic, svgTransformation } = props || {};
        const aboutPin = new Feature({
            geometry: new Point(fromLonLat([long, lat])),
        });
        aboutPin.set('attributes', { iconName, iconColor, svg, svgGraphic, svgTransformation });
        return aboutPin;
    }

    @bind
    addPinsToAboutMap(props) {
        const { latitude, longitude, _geoJson, sync_with_position, iconName, iconColor, svg, svgGraphic, svgTransformation, preserveZoom } = props || {};

        let aboutPin = null;
        const geoJson = new GeoJSON({ featureProjection: 'EPSG:3857' });
        if (_geoJson && (!isDefined(latitude) || !isDefined(longitude))) {
            if (sync_with_position) {
                const aboutPinCenter = this.getCentroidofGeom(_geoJson);
                aboutPin = this.makeFeature(props, aboutPinCenter?.geometry?.coordinates?.[0], aboutPinCenter?.geometry?.coordinates?.[1]);
            } else {
                aboutPin = geoJson.readFeatures(_geoJson)[0];
                aboutPin && aboutPin.set('attributes', { iconName, iconColor, svg, svgGraphic, svgTransformation });
            }
        } else {
            aboutPin = this.makeFeature(props, longitude, latitude);
        }

        const mapLayers = this.getMap() && this.getMap().getLayers();
        if (mapLayers) {
            let aboutMapLayer = this.getMap()
                .getLayers()
                .array_.find(layer => layer.values_.title === MAP_MARKER_LAYER);

            if (aboutMapLayer) {
                this.getMap().removeLayer(aboutMapLayer);
            }
            const vectorSource = new VectorSource({
                features: [aboutPin],
            });

            const markerZIndex = this.getNextLayerZIndex() + 1000;
            
            aboutMapLayer = new VectorLayer({
                source: vectorSource,
                title: MAP_MARKER_LAYER,
                style: this.createDefaultStyle(aboutPin, true),
                zIndex: markerZIndex,
            });
            this.getMap().addLayer(aboutMapLayer);
            
            applyLayerZIndex(aboutMapLayer, markerZIndex);
            if (!preserveZoom) {
                this.zoomToMapLayer(aboutMapLayer, DEFAULT_ZOOM_LEVEL);
            }
            this.getMap().render();
        }
    }

    @bind
    addHistoryFeaturesToMap(historyFeatures) {
        const aboutMapLayer = this.getMap()
            .getLayers()
            .array_.find(layer => layer.values_.title === MAP_MARKER_LAYER);
        aboutMapLayer.getSource().clear();
        historyFeatures.forEach(feature => {
            const { date } = feature.get('attributes') || {};
            const newStyle = new Style({
                image: new CircleStyle({
                    radius: 5,
                    fill: new Fill({ color: '#3D64B7' }),
                    stroke: new Stroke({
                        color: 'rgba(255, 255, 255, 0.7)',
                        width: 1,
                    }),
                }),
                text: new Text({
                    text: date,
                    backgroundFill: new Fill({ color: 'rgba(0, 16, 51, 0.67)' }),
                    fill: new Fill({ color: 'white' }),
                    offsetX: -20,
                    offsetY: 20,
                }),
            });
            feature.setStyle(newStyle);
            aboutMapLayer.getSource().addFeature(feature);
        });

        for (let i = 0; i < historyFeatures.length; i++) {
            const startingCoords = historyFeatures[i].getGeometry().getCoordinates();
            const endingCoords = historyFeatures[i + 1].getGeometry().getCoordinates();
            if (endingCoords.length && endingCoords.length) {
                const points = [
                    [startingCoords[0], startingCoords[1]],
                    [endingCoords[0], endingCoords[1]],
                ];
                const lineFeature = new Feature({
                    geometry: new LineString(points),
                });
                const lineStyle = new Style({
                    stroke: new Stroke({ color: 'blue', width: 3 }),
                });
                lineFeature.setStyle(lineStyle);
                aboutMapLayer.getSource().addFeature(lineFeature);
            }
        }

        this.getMap().render();
    }

    @bind
    createDefaultStyle(feature, isProgress = false) {
        return singleFeatureStyle({ feature, map: this.map, selected: isProgress });
    }

    @bind
    getLayerFeatures(layer) {
        const features = layer?.getSource() && layer.getSource().getFeatures();
        if (features?.length) return features;
    }

    @bind
    removeLayerSelection() {
        const map = this.getMap();
        if (this.select) {
            map.removeInteraction(this.select);
        }
    }

    @bind
    clearRemovedFeatures() {
        return (this.featuresRemoved = []);
    }

    @bind
    setEraserStatus(status) {
        this.eraserStatus = status;
    }

    @bind
    getEraserStatus() {
        return this.eraserStatus;
    }

    @bind
    removeDrawingFeature(layerId, drawingLayer, savedDrawingLayer, addFeaturesToHistory) {
        const map = this.getMap();
        this.select = new Select({
            condition: mapBrowserEvent => {
                return click(mapBrowserEvent);
            },
            layers: [savedDrawingLayer, drawingLayer],
            hitTolerance: 50,
        });
        if (this.select !== null) {
            map.addInteraction(this.select);
            this.select.on('select', async evt => {
                const features = evt.selected;
                const deselectedFeatures = evt.deselected;
                if (features.length) {
                    features.forEach(f => {
                        const currentFeatures = this.getLayerFeatures(drawingLayer);
                        try {
                            currentFeatures && drawingLayer.getSource().removeFeature(f);
                            const convertoGeoJson = JSON.parse(this.convertFeatureToGeoJson(f));
                            convertoGeoJson && addFeaturesToHistory(convertoGeoJson);
                        } catch (e) {}
                    });
                    features.forEach(f => {
                        const savedFeatures = this.getLayerFeatures(savedDrawingLayer);
                        try {
                            savedFeatures && savedDrawingLayer.getSource().removeFeature(f);
                        } catch (e) {}
                    });
                    deselectedFeatures.forEach(feature => {
                        if (feature.getGeometry() instanceof LineString) {
                            feature.setStyle(null);
                        }
                    });
                    this.map.render();
                }
            });
        }
    }

    @bind
    refReplacer() {
        const m = new Map(),
            v = new Map();
        let init = null;

        return (field, value) => {
            const p = m.get(this) + (Array.isArray(this) ? `[${field}]` : '.' + field);
            const isComplex = value === Object(value);

            if (isComplex) m.set(value, p);

            const pp = v.get(value) || '';
            const path = p.replace(/undefined\.\.?/, '');
            let val = pp ? `#REF:${pp[0] === '[' ? '$' : '$.'}${pp}` : value;

            // eslint-disable-next-line no-unused-expressions
            !init ? (init = value) : val === init ? (val = '#REF:$') : 0;
            if (!pp && isComplex) v.set(value, path);

            return val;
        };
    }

    @bind
    addPinToClassAboutMap(props) {
        const { iconColor, latitude, longitude, svg, svgTransformation } = props || {};
        const aboutPin = new Feature({
            geometry: new Point(fromLonLat([longitude, latitude])),
        });

        const classAbout = true;
        aboutPin.set('attributes', { iconName: null, iconColor, svg, svgTransformation, classAbout });

        const mapLayers = this.getMap() && this.getMap().getLayers();
        if (mapLayers) {
            let aboutMapLayer = this.getMap()
                .getLayers()
                .array_.find(layer => layer.values_.title === 'Class About Map layer');

            if (aboutMapLayer) {
                this.getMap().removeLayer(aboutMapLayer);
            }
            const vectorSource = new VectorSource({
                features: [aboutPin],
            });

            const markerZIndex = this.getNextLayerZIndex() + 1000;
            
            aboutMapLayer = new VectorLayer({
                source: vectorSource,
                title: 'Class About Map layer',
                style: this.createDefaultStyle(aboutPin, true),
                zIndex: markerZIndex,
            });
            this.getMap().addLayer(aboutMapLayer);
            
            // Apply the z-index to ensure it's properly set
            applyLayerZIndex(aboutMapLayer, markerZIndex);
            this.getMap().render();
        }
    }

    @bind
    getCentroidofGeom(_geoJson) {
        return turf.centroid(_geoJson);
    }

    @bind
    removeHightLightFeatures() {
        const highLightLayer = this.findHighLightLayer();
        highLightLayer && highLightLayer.getSource().clear();
    }

    @bind
    clearHighlightIfLayerSelected(layerId) {
        const selectedLayers = this.checkSelectedLayer();
        const isSelected = selectedLayers?.some(layer => 
            layer.values_?.attributes?.id === layerId
        );
        if (isSelected) {
            this.removeHightLightFeatures();
            return true;
        }
        return false;
    }


    @bind
    restoreHighlightIfLayerSelected(layerId) {
        const selectedLayers = this.checkSelectedLayer();
        const isSelected = selectedLayers?.some(layer => 
            layer.values_?.attributes?.id === layerId
        );
        
        if (isSelected) {
            this.highLightFeatures(layerId, true);
            return true;
        }
        return false;
    }

    @bind
    resetLayerStyle(layer) {
        if (layer?.getSource()) {
            if (layer.getSource() instanceof TileLayer || layer.getSource() instanceof TileWMS) {
                return null;
            }
            const layerFeatures = layer.getSource().getFeatures();
            if (layerFeatures && layerFeatures.length) {
                layerFeatures.forEach(feature => {
                    feature.setStyle(entityPinStyle({ feature, map: this.map }));
                });
            }
            this.map.render();
        }
    }

    @bind
    checkLayerVisibility(layer) {
        return layer?.getVisible();
    }

    @bind
    waitForLayer(ms) {
        return new Promise(resp => setTimeout(resp, ms));
    }

    @bind
    addHighlightLayer() {
        const highlightSource = new VectorSource();
        const highlightLayer = new VectorLayer({
            source: highlightSource,
            title: 'Highlight layer',
            attributes: {
                id: '9b1caeb6-243f-11ed-8f33-0242ac130009',
            },
            name: '9b1caeb6-243f-11ed-8f33-0242ac130009',
            style: new Style({
                image: new CircleStyle({
                    radius: 20,
                    stroke: new Stroke({ color: 'rgba(0, 81, 255, 0.3)', width: 25 }),
                }),
            }),
        });

        applyLayerZIndex(highlightLayer, 1000);
        this.getMap().addLayer(highlightLayer);
    }

    @bind
    checkSelectedLayer() {
        const layersGroup = this.getMap()
            .getLayers()
            .array_.find(layer => layer.values_.title === 'allLayers');

        return layersGroup?.getLayers()?.array_?.filter(lyr => {
            const selectedLayer = getSelectedLayerProperty(lyr);
            if (selectedLayer) {
                return lyr;
            }
        });
    }

    @bind
    getAssets(token) {
        const cesiumHeader = new Headers();
        cesiumHeader.append('Authorization', token);

        const requestOptions = {
            method: 'GET',
            headers: cesiumHeader,
            redirect: 'follow',
        };

        return (
            fetch('https://api.cesium.com/v1/assets', requestOptions)
                .then(response => response.text())
                .then(result => {
                    const parsedResult = JSON.parse(result);
                    const { items } = parsedResult;
                    if (items) return items;
                })
                // eslint-disable-next-line no-console
                .catch(error => { /* Handle error silently */ })
        );
    }

    @bind
    getOl3d() {
        return this._ol3d;
    }

    @bind
    get3DVisibility() {
        return this.enable3D;
    }

    @bind
    getEntityLayers() {
        return this.entityLayers;
    }

    @bind
    getCesiumObj() {
        return Cesium;
    }

    @bind
    is3dEnabled() {
        return this.enable3D;
    }

    @bind
    async getAddressLocation(lat, long) {
        const response = await Geocode.fromLatLong(lat, long);
        if (response) {
            const gAddress = get(response, 'data.result') || [];
            const address = Geocode.getAddress(gAddress);
            return { longitude: long, latitude: lat, address: { add_type: 'Physical', ...address } };
        } else {
            // eslint-disable-next-line no-console
            // Could not find address for this location
            return { longitude: long, latitude: lat };
        }
    }

    @bind
    getSelectedBaseLayer() {
        const allBaseLayers = this.getLayersGroup('baseLayers');
        return allBaseLayers && allBaseLayers.getLayers()?.array_?.find(({ values_ }) => values_.visible === true);
    }

    @bind
    updateBillboardStyle() {
        // Cancel any pending billboard update
        if (this._billboardAnimationFrame) {
            cancelAnimationFrame(this._billboardAnimationFrame);
        }
        
        // Schedule new billboard update
        this._billboardAnimationFrame = requestAnimationFrame(() => {
            const zoomLevel = this.map.getView().getZoom();
            const dataSourceCollection = this._ol3d?.getDataSources();
            if (dataSourceCollection) {
                this.findGeoJsonDataSource(dataSourceCollection, zoomLevel);
            }
            this._billboardAnimationFrame = null;
        });
    }

    @bind
    findGeoJsonDataSource(dataSourceCollection, zoomLevel) {
        dataSourceCollection._dataSources.forEach(dataSource => {
            if (dataSource instanceof Cesium.GeoJsonDataSource) {
                const layerId = dataSource?._name;
                const mapLayer = layerId && this.findLayerById(layerId);
                const twoDOpacity = mapLayer?.getOpacity();
                this.updateStyleBasedOnZoom(dataSource, zoomLevel, twoDOpacity);
            }
        });
    }

    @bind
    updateStyleBasedOnZoom(dataSource, zoomLevel, twoDOpacity) {
        dataSource.entities.values.forEach(async entity => {
            const attributes = entity._properties?._attributes?._value;
            const { id, type, model3d, modelHeading, primaryClass, modelScaling } = attributes;
            const entityModel3d = primaryClass?.entityModel3d || null;
            const threeDModel = model3d || entityModel3d;
            const heading = Cesium.Math.toRadians(modelHeading || 135);
            const pitch = 0;
            const roll = 0;
            if (threeDModel && zoomLevel >= 10) {
                const hpr = new Cesium.HeadingPitchRoll(heading, pitch, roll);
                entity.orientation = Cesium.Transforms.headingPitchRollQuaternion(entity.position._value, hpr);
                entity.billboard = undefined; // Remove the existing billboard style
                const imageUrl = getAttachmentUrl(id, type, model3d, true);
                const host = rocketHost.split('/chat')[0];
                const url = `https://${host}` + imageUrl.split('?')[0];
                const modelColor = Cesium.Color.fromCssColorString(attributes?.iconColor);
                // const url = `http://localhost:3000/${imageUrl.split('?')[0]}`;
                const response = await fetch(url);
                const result = await response.text();

                if (result) {
                    entity.model = new Cesium.ModelGraphics({
                        uri: url,
                        color: modelColor,
                        colorBlendMode: Cesium.ColorBlendMode.MIX,
                        colorBlendAmount: 0.5,
                        minimumPixelSize: 128,
                        maximumScale: 20000,
                        scale: (modelScaling && modelScaling * 3) || 1,
                    });
                    entity.billboard.image = null;
                }
            } else {
                entity.model = undefined;
                const { iconColor, iconName } = attributes;
                const icon = iconName || 'map-marker';
                const name = mdiMapping?.[icon] || customIconSet?.[icon];
                const text = String.fromCodePoint(name);
                entity.billboard = new Cesium.BillboardGraphics({
                    image: create3DIconCanvas(text, {
                        strokeColor: '#000000',
                        fontSize: '20',
                        fontFamily: 'Material Design Icons',
                        fillColor: iconColor || '#00BCD4',
                        strokeColor: '#000000',
                        strokeWidth: 0.5,
                        twoAlpha: twoDOpacity,
                    }),
                    verticalOrigin: Cesium.VerticalOrigin.BOTTOM,
                });
                entity.billboard.color = new Cesium.Color(1.0, 1.0, 1.0, twoDOpacity === 100 ? 1 : twoDOpacity);
            }
        });
    }

    @bind
    async refresh3DMapLayers() {


        for (const id in this.entityLayers) {
            const entityLayer = this.entityLayers[id];
            await entityLayer.refresh3DMapLayers(this.getCesiumObj(), this.getOl3d(), this.enable3D);
        }
    }
}

export default olMap;
